import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, AlertCircle, Lock, User, Percent, Building2, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ActivarComisionista() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [invData, setInvData] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    if (!token) { setError('No se proporcionó token de invitación'); setLoading(false); return; }
    const verify = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/comisionistas/verificar-token/${token}`);
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          setError(d.detail || 'Invitación no válida');
        } else {
          setInvData(await res.json());
        }
      } catch { setError('Error al verificar la invitación'); }
      finally { setLoading(false); }
    };
    verify();
  }, [token]);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) { toast.error('Ingresa tu nombre'); return; }
    if (formData.password.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    if (formData.password !== formData.confirmPassword) { toast.error('Las contraseñas no coinciden'); return; }

    setActivating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/comisionistas/activar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, nombre: formData.nombre, password: formData.password })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Error al activar');
      toast.success('Cuenta activada. Bienvenido!');
      navigate('/portal-comisionista');
    } catch (err) {
      toast.error(err.message);
    } finally { setActivating(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#C6A54A] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Invitación no válida</h2>
            <p className="text-zinc-400 mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} className="bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-semibold">
              Ir a Iniciar Sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_lotesys/artifacts/fyrjj46v_Logo%20IBA%C3%91EZ%20para%20desarrollos%20inmobiliarios.png"
            alt="Ibañez Desarrollos" className="w-48 mx-auto mb-4"
          />
        </div>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="text-center border-b border-zinc-800 pb-6">
            <div className="w-16 h-16 bg-[#C6A54A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#C6A54A]" />
            </div>
            <CardTitle className="text-white text-xl">Activa tu cuenta de Comisionista</CardTitle>
            <p className="text-zinc-400 mt-2">
              <span className="text-[#C6A54A] font-semibold">{invData?.invitado_por}</span> te ha invitado
            </p>
          </CardHeader>

          <CardContent className="p-6">
            {/* Invitation details */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <span className="text-zinc-400 text-sm flex items-center gap-2"><Mail className="w-4 h-4" />Tu correo</span>
                <span className="text-white font-medium" data-testid="activar-email-display">{invData?.email}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <span className="text-zinc-400 text-sm flex items-center gap-2"><Building2 className="w-4 h-4" />Fraccionamiento</span>
                <span className="text-white font-medium">{invData?.fraccionamiento_nombre}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <span className="text-zinc-400 text-sm flex items-center gap-2"><Percent className="w-4 h-4" />Comisión base</span>
                <span className="text-[#C6A54A] font-bold text-lg">{invData?.porcentaje_base}%</span>
              </div>
              <div className="p-3 bg-[#C6A54A]/10 border border-[#C6A54A]/30 rounded-lg">
                <p className="text-[#C6A54A] text-sm">Usarás este correo y contraseña para iniciar sesión en el portal de comisionistas.</p>
              </div>
            </div>

            <form onSubmit={handleActivate} className="space-y-4">
              <div>
                <Label className="text-zinc-400">Tu nombre completo</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input data-testid="activar-nombre" value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    placeholder="Nombre completo" className="pl-10 bg-zinc-800 border-zinc-700 text-white" required />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">Crear contraseña</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input data-testid="activar-password" type={showPassword ? 'text' : 'password'}
                    value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Mínimo 6 caracteres" className="pl-10 bg-zinc-800 border-zinc-700 text-white" required />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">Confirmar contraseña</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input data-testid="activar-confirm-password" type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Repite la contraseña" className="pl-10 bg-zinc-800 border-zinc-700 text-white" required />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} className="rounded border-zinc-700" />
                Mostrar contraseñas
              </label>
              <Button data-testid="activar-submit-btn" type="submit" disabled={activating}
                className="w-full h-11 bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-bold">
                {activating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Activar mi Cuenta'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="text-center text-zinc-500 text-xs mt-6">
          Al activar, podrás acceder al portal de comisionista con tus credenciales.
        </p>
      </div>
    </div>
  );
}
